// =============================================================================
// Geometria do catálogo — CÓPIA da conta do servidor
// (backend/src/services/catalogoGeometria.js).
//
// A prévia precisa saber, sem ir ao servidor a cada arrasto, onde cada produto
// cai e de que tamanho fica. As constantes (margens, vãos, proporção da folha)
// vêm do próprio servidor em GET /catalogo/grade, então só a lógica é copiada.
// Mudou a lógica lá, muda aqui — senão a prévia mostra uma coisa e o PDF sai
// outra.
// =============================================================================

export function medirFolha(G, larguraMM, alturaMM) {
  let L = larguraMM * 0.95;
  let A = L / G.FOLHA_PROP;
  const limite = alturaMM * 0.56;
  if (A > limite) { A = limite; L = A * G.FOLHA_PROP; }
  return { L, A, dir: larguraMM * G.RECUO - L * G.VAZIO_DIR, baixo: alturaMM * G.RECUO - A * G.VAZIO_BAIXO };
}

export function medidasCard(G, larguraMM, alturaMM) {
  const f = medirFolha(G, larguraMM, alturaMM);
  return {
    razao: larguraMM / alturaMM,
    padDir: f.L * (1 - G.VAZIO_DIR) * 0.20 / larguraMM * 100,
    padBaixo: f.A * (1 - G.VAZIO_BAIXO) * 0.52 / alturaMM * 100,
    folha: { w: f.L / larguraMM * 100, h: f.A / alturaMM * 100,
      dir: f.dir / larguraMM * 100, baixo: f.baixo / alturaMM * 100 },
  };
}

// Ajuste da gerente dentro do card (cópia de folhaAjustada/tarjaAjustada do
// servidor): folha com escala `e` a partir do canto de baixo à direita e
// deslocamento dx/dy em % do card, sem nunca sair dele; fita do "De:" deslocada.
// `solta` (página livre, igual ao story): a folha pode sair da caixa do produto
// (até 80% dela pra fora) e crescer até 2x.
export function folhaAjustada(m, aj, solta = false) {
  const f = m.folha;
  const eMax = solta ? 2 : Math.min(1.3, (100 - f.dir) / f.w, (100 - f.baixo) / f.h);
  const e = Math.min(eMax, Math.max(0.5, Number(aj?.e) || 1));
  const w = f.w * e, h = f.h * e;
  const [dirMin, dirMax] = solta ? [-0.8 * w, 100 - 0.2 * w] : [f.dir, 100 - w];
  const [baixoMin, baixoMax] = solta ? [-0.8 * h, 100 - 0.2 * h] : [f.baixo, 100 - h];
  const dir = Math.min(dirMax, Math.max(dirMin, f.dir - (Number(aj?.dx) || 0)));
  const baixo = Math.min(baixoMax, Math.max(baixoMin, f.baixo - (Number(aj?.dy) || 0)));
  return { w, h, dir, baixo, e };
}

export function tarjaAjustada(aj, solta = false) {
  return {
    x: Math.min(solta ? 110 : 80, Math.max(solta ? -40 : 0, 4.5 + (Number(aj?.dx) || 0))),
    y: Math.min(solta ? 110 : 92, Math.max(solta ? -30 : 0, 4.5 + (Number(aj?.dy) || 0))),
  };
}

// Tamanho do card em células (colunas x linhas); `grande: true` é o 2x2 de antes.
export const TAMANHOS = { normal: [1, 1], largo: [2, 1], alto: [1, 2], grande: [2, 2] };
export function celulasDoItem(p, cols, linhas) {
  const t = TAMANHOS[p?.tamanho] ? p.tamanho : (p?.grande ? 'grande' : 'normal');
  const [c, l] = TAMANHOS[t];
  return c > cols || l > linhas ? { c: 1, l: 1 } : { c, l };
}

// Grade de cols x linhas; cada item ocupa as células do tamanho dele. O que não cabe vai em `sobra`.
export function encaixarBloco(itens, cols, linhas) {
  const g = Array.from({ length: linhas }, () => Array(cols).fill(false));
  const cabe = (lin, col, l, c) => {
    if (lin + l > linhas || col + c > cols) return false;
    for (let i = lin; i < lin + l; i++) for (let j = col; j < col + c; j++) if (g[i][j]) return false;
    return true;
  };
  const postos = [], sobra = [];
  for (const p of itens) {
    const { c, l } = celulasDoItem(p, cols, linhas);
    let posto = null;
    for (let lin = 0; lin < linhas && !posto; lin++) {
      for (let col = 0; col < cols && !posto; col++) {
        if (cabe(lin, col, l, c)) posto = { lin, col, l, c };
      }
    }
    if (!posto) { sobra.push(p); continue; }
    for (let i = posto.lin; i < posto.lin + l; i++) for (let j = posto.col; j < posto.col + c; j++) g[i][j] = true;
    postos.push({ p, ...posto });
  }
  return { postos, sobra };
}

// Mesma conta de layoutPagina do servidor: blocos empilhados, a altura da linha
// divide o espaço da página entre todas as linhas, com teto pra não esticar.
export function layoutPagina(G, blocos, tipo) {
  const a = G.AREA[tipo];
  const util = G.A4_L - a.esq - a.dir;
  const totalLinhas = blocos.reduce((s, b) => s + b.linhas, 0) || 1;
  const vaos = blocos.reduce((s, b) => s + (b.linhas - 1) * G.VAO, 0)
    + Math.max(0, blocos.length - 1) * G.VAO_BLOCO;
  const base = (G.A4_A - a.topo - a.base - vaos) / totalLinhas;

  let y = a.topo;
  return blocos.map((b) => {
    const cardL = (util - (b.cols - 1) * G.VAO) / b.cols;
    const alt = Math.min(base, cardL * G.TETO);
    const { postos, sobra } = encaixarBloco(b.itens, b.cols, b.linhas);
    const altBloco = b.linhas * alt + (b.linhas - 1) * G.VAO;
    const cards = postos.map(({ p, lin, col, l, c }) => {
      const w = c * cardL + (c - 1) * G.VAO;
      const h = l * alt + (l - 1) * G.VAO;
      return { p, lin, col, l, c, x: a.esq + col * (cardL + G.VAO), y: y + lin * (alt + G.VAO), w, h,
        medidas: medidasCard(G, w, h) };
    });
    const faixas = [];
    if (b.estilo === 'amarela') {
      for (let lin = 0; lin < b.linhas; lin++) {
        faixas.push({ cor: 'amarela', x: a.esq - 1.5, y: y + lin * (alt + G.VAO) + alt * 0.44, w: util + 3, h: alt * 0.30 });
      }
    } else if (b.estilo === 'branca') {
      faixas.push({ cor: 'branca', x: a.esq - 1.5, y: y - 1.5, w: util + 3, h: altBloco + 3 });
    }
    // Caixa do bloco inteiro (mm) — área onde dá pra soltar produto.
    const caixa = { x: a.esq, y, w: util, h: altBloco };
    const r = { ...b, y, alt, altBloco, cardL, cards, faixas, sobra, caixa, solto: b.estilo !== 'quadros' };
    y += altBloco + G.VAO_BLOCO;
    return r;
  });
}

/** Cabe mais um item neste bloco? */
export function cabeNoBloco(bloco, item) {
  return encaixarBloco([...bloco.itens, item], bloco.cols, bloco.linhas).sobra.length === 0;
}

// Altura de linha que a página teria com esses blocos. Abaixo de G.ALTURA_MIN a
// folha do preço fica ilegível — a tela usa isto pra não deixar passar disso.
export function alturaLinhaPagina(G, blocos, tipo) {
  const a = G.AREA[tipo];
  const total = blocos.reduce((s, b) => s + b.linhas, 0) || 1;
  const vaos = blocos.reduce((s, b) => s + (b.linhas - 1) * G.VAO, 0) + Math.max(0, blocos.length - 1) * G.VAO_BLOCO;
  return (G.A4_A - a.topo - a.base - vaos) / total;
}

// --- Página livre --------------------------------------------------------------
// Cada produto tem a própria caixa, em % da página (x/w da largura, y/h da
// altura). Cópia de caixaLivre do servidor (limites: cabe na página e não fica
// pequena demais pra folha do preço).
export function caixaLivre(c) {
  const w = Math.min(100, Math.max(8, Number(c?.w) || 22));
  const h = Math.min(100, Math.max(6, Number(c?.h) || 18));
  return { w, h, x: Math.min(100 - w, Math.max(0, Number(c?.x) || 0)), y: Math.min(100 - h, Math.max(0, Number(c?.y) || 0)) };
}

// Área útil da página em % (abaixo do cabeçalho, acima do rodapé).
export function areaLivre(G, tipo) {
  const a = G.AREA[tipo];
  return { x0: a.esq / G.A4_L * 100, x1: 100 - a.dir / G.A4_L * 100, y0: a.topo / G.A4_A * 100, y1: 100 - a.base / G.A4_A * 100 };
}

// N caixas arrumadas em grade na área útil (o "Organizar" e a 1ª arrumação):
// colunas pela proporção da área, mesmo vão e mesmo teto da grade de blocos.
export function gradeLivre(G, tipo, n) {
  if (!n) return [];
  const ar = areaLivre(G, tipo);
  const W = (ar.x1 - ar.x0) / 100 * G.A4_L, H = (ar.y1 - ar.y0) / 100 * G.A4_A;   // mm
  const cols = Math.max(1, Math.min(5, Math.round(Math.sqrt(n * W / H))));
  const linhas = Math.ceil(n / cols);
  const cw = (W - (cols - 1) * G.VAO) / cols;
  const ch = Math.min((H - (linhas - 1) * G.VAO) / linhas, cw * G.TETO);
  return Array.from({ length: n }, (_, k) => ({
    x: ar.x0 + (k % cols) * (cw + G.VAO) / G.A4_L * 100,
    y: ar.y0 + Math.floor(k / cols) * (ch + G.VAO) / G.A4_A * 100,
    w: cw / G.A4_L * 100, h: ch / G.A4_A * 100,
  }));
}

// Primeira vaga numa grade de 4 colunas (cards do tamanho da grade 4 × 5) que não
// bate em nenhuma caixa já colocada. Sem vaga: null.
export function vagaLivre(G, tipo, ocupadas) {
  const ar = areaLivre(G, tipo);
  const W = (ar.x1 - ar.x0) / 100 * G.A4_L, H = (ar.y1 - ar.y0) / 100 * G.A4_A;
  const cw = (W - 3 * G.VAO) / 4, ch = Math.min(cw * 1.1, H);
  const linhas = Math.max(1, Math.floor((H + G.VAO) / (ch + G.VAO)));
  const bate = (a, b) => {
    const ix = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x), iy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
    return ix > 0 && iy > 0 && ix * iy > a.w * a.h * 0.15;
  };
  for (let i = 0; i < linhas; i++) {
    for (let j = 0; j < 4; j++) {
      const c = { x: ar.x0 + j * (cw + G.VAO) / G.A4_L * 100, y: ar.y0 + i * (ch + G.VAO) / G.A4_A * 100,
        w: cw / G.A4_L * 100, h: ch / G.A4_A * 100 };
      if (!ocupadas.some(o => o && bate(c, o))) return c;
    }
  }
  return null;
}
